import * as React from 'react';
import { DataSignal } from 's-js';
import * as cx from 'classnames';
import SuperCritical, { SuperCriticalComponent } from 'lib/supercritical';
import TZDate from 'lib/TZDate';
import { tzmoment } from 'lib/tzmoment';
import { noDefault } from 'lib/eventWrappers';
import { ActivityFeedNewReservation } from 'data/models/ActivityFeedResponse';
import { BerthReservation } from 'data/models/BerthReservation';
import { BerthReservationStatus, BerthReservationStatusDisplayName } from 'server/enums/BerthReservation';
import { DomainSummary } from 'data/models/DomainSummary';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { RecurringBatch } from 'data/models/RecurringBatch';
import { ScheduleDate } from 'data/models/ScheduleDate';
import { TopicCard } from 'data/models/TopicCard';
import {
    ActivityFeed,
    ActivityFeedTab,
    LineTimeType,
    NewReservationModel,
    NewBatchModel,
    ChangedReservationModel,
    ChangedBatchModel,
    ChangedTopicCardModel,
    ConflictedReservationModel,
    ConflictedBatchModel,
    MissingActualModel,
    UserModel
} from './activityfeed';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { TimeAgoTooltip } from 'portcall/pctooltip/view';
import { PersonProfilePopupView } from 'portcall/personprofilepopup/view';
import { ConflictReasonText } from 'portcall/issues/view';

const LINE_TIME_FORMAT = 'MM/DD/YYYY [at] HH:mm';

export const ActivityFeedView = SuperCritical(({ model: m } : { model : ActivityFeed }) =>
    <section className="activity-feed">
        <header>Activity Feed</header>
        <TabBar model={m} />
        <NewTabContent model={m} />
        <ChangesTabContent model={m} />
        <ConflictsTabContent model={m} />
        <OpenTabContent model={m} />
    </section>
);

let TabBar = SuperCritical(({ model: m } : { model : ActivityFeed }) =>
    <div className="activity-feed-tabs">
        <TabSelector model={m} name="New"       className="new"       tab={ActivityFeedTab.New}       items={m.New} />
        <TabSelector model={m} name="Changes"   className="changes"   tab={ActivityFeedTab.Changes}   items={m.Changes} />
        <TabSelector model={m} name="Conflicts" className="conflicts" tab={ActivityFeedTab.Conflicts} items={m.Conflicts} />
        <TabSelector model={m} name="Open"      className="open"      tab={ActivityFeedTab.Open}      items={m.Open} />
    </div>
);

let TabSelector = SuperCritical(({ model: m, tab, items, name, className } : {
    model : ActivityFeed,
    tab : ActivityFeedTab,
    items : () => any[],
    name : string,
    className : string
}) =>
    <div className={cx('activity-feed-tab-selector pc-card', className, {
            selected: tab === m.Tab()
        })}
        onClick={() => m.Tab(tab)}>
        <div className="tab-name">{name}</div>
        <div className="tab-count">{items().length}</div>
    </div>
);

abstract class ActivityFeedTabContent<T> extends SuperCriticalComponent<{ model : ActivityFeed }> {
    constructor(props : { model : ActivityFeed }, private readonly tab : ActivityFeedTab) {
        super(props);
    }

    abstract items(m : ActivityFeed) : T[];
    abstract renderItem(item : T, m : ActivityFeed) : React.ReactElement<any>;

    srender({ model: m } : { model : ActivityFeed }) {
        if (m.Tab() !== this.tab) return null;
        return (
            <div>
                {this.items(m).map(item => this.renderItem(item, m))}
            </div>
        );
    }
}

class NewTabContent extends ActivityFeedTabContent<NewReservationModel | NewBatchModel> {
    constructor(props : { model : ActivityFeed }) {
        super(props, ActivityFeedTab.New);
    }

    items(m : ActivityFeed) {
        return m.New();
    }

    renderItem(item : NewReservationModel | NewBatchModel, m : ActivityFeed) {
        return (
            <NewTabItem item={item} model={m} key={item.Key()} />
        );
    }
}

class ChangesTabContent extends ActivityFeedTabContent<ChangedReservationModel | ChangedBatchModel | ChangedTopicCardModel> {
    constructor(props : { model : ActivityFeed }) {
        super(props, ActivityFeedTab.Changes);
    }

    items(m : ActivityFeed) {
        return m.Changes();
    }

    renderItem(item : ChangedReservationModel | ChangedBatchModel | ChangedTopicCardModel, m : ActivityFeed) {
        return (
            <ChangesTabItem item={item} model={m} key={item.Key()} />
        );
    }
}

class ConflictsTabContent extends ActivityFeedTabContent<ConflictedReservationModel | ConflictedBatchModel> {
    constructor(props : { model : ActivityFeed }) {
        super(props, ActivityFeedTab.Conflicts);
    }

    items(m : ActivityFeed) {
        return m.Conflicts();
    }

    renderItem(item : ConflictedReservationModel | ConflictedBatchModel, m : ActivityFeed) {
        return (
            <ConflictsTabItem item={item} model={m} key={item.Key()} />
        );
    }
}

class OpenTabContent extends ActivityFeedTabContent<MissingActualModel> {
    constructor(props : { model : ActivityFeed }) {
        super(props, ActivityFeedTab.Open);
    }

    items(m : ActivityFeed) {
        return m.Open();
    }

    renderItem(item : MissingActualModel, m : ActivityFeed) {
        return (
            <OpenTabItem item={item} model={m} key={item.Key()} />
        );
    }
}

let NewTabItem = SuperCritical(({ item, model: m } : { item : NewReservationModel | NewBatchModel, model : ActivityFeed }) =>
    item.Type === 'reservation' ?
        <NewReservationView item={item} model={m} /> :
        <NewBatchView       item={item} model={m} />
);

let NewReservationView = SuperCritical(({ item, model: m } : { item : NewReservationModel, model : ActivityFeed }) =>
    <div className="pc-card tab-item-card">
        {item.Model.Reservation() && <ReservationActionsView reservation={item.Model.Reservation()!} showActions={item.ShowActions} />}
        <CardAttributionWithUserAndDate user={item.Requestor} date={() => item.Model.Reservation() && item.Model.Reservation()!.RequestDate()} now={m.EventNow} />
        <NewReservationText item={item} />
        {item.Model.Reservation() && <ReservationSummaryView reservation={item.Model.Reservation()!} />}
    </div>
);

let NewReservationText = SuperCritical(({ item } : { item : NewReservationModel }) =>
    <div>
        Submitted a new reservation for {item.Model.Reservation() && item.Model.Reservation()!.Itinerary() && tzmoment(item.Model.Reservation()!.Itinerary()!.Arrival.Effective()).format('dddd ' + LINE_TIME_FORMAT)}.
    </div>
);

let NewBatchView = SuperCritical(({ item, model: m } : { item : NewBatchModel, model : ActivityFeed }) =>
    <div className="pc-card tab-item-card">
        <BatchActionsView batch={item.Model} showActions={item.ShowActions} />
        <CardAttributionWithUserAndDate user={item.Requestor} date={item.Model.RequestDate} now={m.EventNow} />
        <NewBatchText item={item} />
        <BatchSummaryView batch={item.Model} firstReservationPoint={item.FirstReservationPoint} />
    </div>
);

let NewBatchText = SuperCritical(({ item } : { item : NewBatchModel }) =>
    <div>
        Submitted a new batch for {item.FirstPortCall() && item.FirstPortCall()!.DisplayVesselIdentifier()}.
    </div>
);

let ChangesTabItem = SuperCritical(({ item, model: m } : { item : ChangedReservationModel | ChangedBatchModel | ChangedTopicCardModel, model : ActivityFeed }) =>
    item.Type === 'reservation' ? <ChangedReservationView item={item} model={m} /> :
    item.Type === 'batch'       ? <ChangedBatchView       item={item} model={m} /> :
                                  <ChangedTopicCardView   item={item} model={m} />
);

let ChangedReservationView = SuperCritical(({ item, model: m } : { item : ChangedReservationModel, model : ActivityFeed }) => {
    const date = () => item.Model.Event() && item.Model.Event()!.Date();
    return (
        <div className="pc-card tab-item-card">
            {item.Model.Reservation() && <ReservationActionsView reservation={item.Model.Reservation()!} showActions={item.ShowActions} />}
            {item.Model.Event() && item.Model.Event()!.UserId() ?
                <CardAttributionWithUserAndDate   user={item.Changer} date={date} now={m.EventNow} /> :
                <CardAttributionWithDomainAndDate domain={m.Domain}   date={date} now={m.EventNow} />
            }
            <ChangedReservationText item={item} />
            {item.Model.Reservation() && <ReservationSummaryView reservation={item.Model.Reservation()!} />}
        </div>
    );
});

let ChangedReservationText = SuperCritical(({ item } : { item : ChangedReservationModel }) =>
    <div>
        Changed status to
        {' '}
        {BerthReservationStatusDisplayName[item.Model.NewStatus()]}.
    </div>
);

let ChangedBatchView = SuperCritical(({ item, model: m } : { item : ChangedBatchModel, model : ActivityFeed }) => {
    const date = () => item.Model.Event() && item.Model.Event()!.Date();
    return (
        <div className="pc-card tab-item-card">
            {item.Model.Batch() && <BatchActionsView batch={item.Model.Batch()!} showActions={item.ShowActions} />}
            {item.ChangerId() ?
                <CardAttributionWithUserAndDate   user={item.Changer} date={date} now={m.EventNow} /> :
                <CardAttributionWithDomainAndDate domain={m.Domain}   date={date} now={m.EventNow} />
            }
            <ChangedBatchText item={item} />
            {item.Model.Batch() && <BatchSummaryView batch={item.Model.Batch()!} firstReservationPoint={item.FirstReservationPoint} />}
        </div>
    )
});

let ChangedBatchText = SuperCritical(({ item } : { item : ChangedBatchModel }) =>
    <div>
        Changed
        {' '}
        {item.Model.ReservationChanges().length}
        {' '}
        out of
        {' '}
        {item.Model.Batch() && item.Model.Batch()!.Reservations().length}
        {' '}
        batch reservations status to
        {' '}
        {item.NewStatus() === null ? null : BerthReservationStatusDisplayName[item.NewStatus()!]}.
    </div>
);

let ChangedTopicCardView = SuperCritical(({ item, model: m } : { item : ChangedTopicCardModel, model : ActivityFeed }) => {
    const date = () => item.Model.Event() && item.Model.Event()!.Date();
    return (
        <div className="pc-card tab-item-card">
            {item.Model.Card() && <TopicCardActionsView card={item.Model.Card()!} showActions={item.ShowActions} />}
            {item.Model.Event() && item.Model.Event()!.UserId() ?
                <CardAttributionWithUserAndDate   user={item.Changer} date={date} now={m.EventNow} /> :
                <CardAttributionWithDomainAndDate domain={m.Domain}   date={date} now={m.EventNow} />
            }
            <ChangedTopicCardText item={item} />
            {item.Model.Card() && <TopicCardSummaryView card={item.Model.Card()!} />}
        </div>
    );
});

let ChangedTopicCardText = SuperCritical(({ item } : { item : ChangedTopicCardModel }) =>
    <div>Changed status for topic card.</div>
);

let ConflictsTabItem = SuperCritical(({ item, model: m } : { item : ConflictedReservationModel | ConflictedBatchModel, model : ActivityFeed }) =>
    item.Type === 'reservation' ?
        <ConflictedReservationView item={item} model={m} /> :
        <ConflictedBatchView       item={item} model={m} />
);

let ConflictedReservationView = SuperCritical(({ item, model: m } : { item : ConflictedReservationModel, model : ActivityFeed }) =>
    <div className="pc-card tab-item-card">
        <ReservationActionsView reservation={item.Model} showActions={item.ShowActions} />
        <CardAttributionWithDomain domain={m.Domain} />
        <ConflictedReservationText item={item} />
        <ReservationSummaryView reservation={item.Model} />
    </div>
);

let ConflictedReservationText = SuperCritical(({ item } : { item : ConflictedReservationModel }) =>
    <div className="conflict-item-text">
        <VesselNameView point={item.Model.Itinerary} />
        {' '}
        is in conflict because it
        {' '}
        {/* TH 11/18/2016 -- This draft does one card per reservation and just describes the highest priority issue.
            An alternative would be to do a card per issue, instead of a card per reservation. */}
        {ConflictReasonText(item.Model.Issues)}.
    </div>
);

let ConflictedBatchView = SuperCritical(({ item, model: m } : { item : ConflictedBatchModel, model : ActivityFeed }) =>
    <div className="pc-card tab-item-card">
        <BatchActionsView batch={item.Model} showActions={item.ShowActions} />
        <CardAttributionWithDomain domain={m.Domain} />
        <ConflictedBatchText item={item} />
        <BatchSummaryView batch={item.Model} firstReservationPoint={item.FirstReservationPoint} showConflicted />
    </div>
);

let ConflictedBatchText = SuperCritical(({ item } : { item : ConflictedBatchModel }) =>
    <div>
        {item.Model.Reservations().filter(r => r.Status() === BerthReservationStatus.Conflicted).length}
        {' '}
        out of
        {' '}
        {item.Model.Reservations().length}
        {' '}
        reservations are in conflict.
    </div>
);

let OpenTabItem = SuperCritical(({ item, model: m } : { item : MissingActualModel, model : ActivityFeed }) =>
    <div className="pc-card tab-item-card">
        <ReservationActionsView reservation={item.Model} showActions={item.ShowActions} />
        <CardAttributionWithDomain domain={m.Domain} />
        <OpenItemText item={item} lineType={item.LineType} />
        <ReservationSummaryView reservation={item.Model} showOnly={item.LineType} />
    </div>
);

let OpenItemText = SuperCritical(({ item, lineType } : { item : MissingActualModel, lineType : LineTimeType }) =>
    <div className="open-item-text">
        <VesselNameView point={item.Model.Itinerary} />
        {' '}
        is still missing
        {' '}
        {lineType === LineTimeType.FirstLine ? 'First Line Actual' : 'Last Line Actual'}.
    </div>
);

let ReservationActionsView = SuperCritical(({ reservation: res, showActions } : { reservation : BerthReservation, showActions : DataSignal<boolean> }) =>
    <ActivityActionsContainer showActions={showActions}>
        <div className="action-item">
            {res.Itinerary() && <a href={'#!portcallprofile?id=' + res.Itinerary()!.PortCallId()}>View PortCall</a>}
        </div>
        {res.Status() !== BerthReservationStatus.Cancelled &&
            <div className="action-item">
                {res.Itinerary() && <a href={'#!schedulegrid?date=' + tzmoment(res.Itinerary()!.Arrival.Effective()).add(-1, 'days').format('YYYY-MM-DD') + '&resid=' + res.Id()}>View on Berths</a>}
            </div>
        }
    </ActivityActionsContainer>
);

let BatchActionsView = SuperCritical(({ batch, showActions } : { batch : RecurringBatch, showActions : DataSignal<boolean> }) =>
    <ActivityActionsContainer showActions={showActions}>
        <div className="action-item">
            <a href={'#!reservationsinbatch?batchId=' + batch.Id()}>View Batch</a>
        </div>
    </ActivityActionsContainer>
);

let TopicCardActionsView = SuperCritical(({ card, showActions } : { card : TopicCard, showActions : DataSignal<boolean> }) =>
    <ActivityActionsContainer showActions={showActions}>
        <div className="action-item">
            <a href={`#!topiccards?id=${card.PortCallId()}&cardId=${card.Id()}`}>View Card</a>
        </div>
    </ActivityActionsContainer>
);

let ActivityActionsContainer = SuperCritical(({ showActions, children } : { showActions : DataSignal<boolean>, children? : any }) =>
    <div className="activity-actions-container">
        <a href="" className="fa fa-bars actions-toggle" onClick={noDefault(() => showActions(!showActions()))}></a>
        {showActions() &&
            <PCDismissible className="activity-actions-menu pc-card" onDismiss={() => showActions(false)}>
                {children}
            </PCDismissible>
        }
    </div>
);

abstract class CardAttributionBase<TProps> extends SuperCriticalComponent<TProps> {
    abstract thumb : (props : TProps) => React.ReactElement<any> | null;
    abstract textPrimary : (props : TProps) => React.ReactElement<any> | string | null;
    abstract textSecondary : ((props : TProps) => React.ReactElement<any>) | null;

    srender(props : TProps) {
        return (
            <div className="card-attribution">
                <div className="attribution-thumb">{this.thumb(props)}</div>
                <div className="attribution-text">
                    <div className="attribution-text-primary">{this.textPrimary(props)}</div>
                    {this.textSecondary && <div className="attribution-text-secondary pc-text-smalllabel">{this.textSecondary(props)}</div>}
                </div>
            </div>
        );
    }
}

interface WithDomain {
    domain : DomainSummary;
}

interface WithDate {
    date : () => TZDate | null;
    now : () => TZDate;
}

interface WithUser {
    user : () => UserModel | null;
}

let bollardThumb = () => <span className="attribution-icon icon-bollard"></span>;
let domainText = ({ domain } : WithDomain) => <span className="domain-url">{domain.DisplayableUrl()}</span>;

class CardAttributionWithDomain extends CardAttributionBase<WithDomain> {
    thumb = bollardThumb;
    textPrimary = domainText;
    textSecondary = null;
}

class CardAttributionWithDomainAndDate extends CardAttributionBase<WithDomain & WithDate> {
    thumb = bollardThumb;
    textPrimary = domainText;
    textSecondary = ({ date, now } : WithDate) => <DateTooltip date={date} now={now} />;
}

class CardAttributionWithUserAndDate extends CardAttributionBase<WithUser & WithDate> {
    thumb = ({ user } : WithUser) => user() && <img className="attribution-img" src={user()!.Model.Image.ThumbUrl()} />;

    textPrimary = ({ user } : WithUser) => user() &&
        <div>
            <a href="" onClick={noDefault(user()!.ToggleProfile)}>{user()!.Model.FullName()}</a>
            <PersonProfilePopupView modelContainer={user()!.ProfilePopup} />
        </div>;

    textSecondary = ({ user, date, now } : WithUser & WithDate) =>
        <div>
            <span>{user() && user()!.Model.Organization() && user()!.Model.Organization()!.Name()}</span>
            {' '}
            <span>|</span>
            {' '}
            <DateTooltip className="date-tooltip-inline" date={date} now={now} />
        </div>;
}

let DateTooltip = SuperCritical(({ date, now, className } : { className? : string } & WithDate) => {
    if (!date()) return null;
    return (
        <TimeAgoTooltip date={() => date()!} now={now} className={className} />
    );
});

let ReservationSummaryView = SuperCritical(({ reservation: res, showOnly } : { reservation : BerthReservation, showOnly? : LineTimeType }) =>
    <div className={cx('reservation-summary', {
            conflicted: res.Status() === BerthReservationStatus.Conflicted,
            booked: res.Status() === BerthReservationStatus.Booked,
            inport: !!res.Itinerary() && !!res.Itinerary()!.Arrival.Actual() && !res.Itinerary()!.Departure.Actual()
        })}>
        <VesselNameView point={res.Itinerary} />
        <BerthNameView point={res.Itinerary} />
        {showOnly !== LineTimeType.LastLine &&
            <LineTimeView reservation={res} name="First Line" lineTime={pt => pt.Arrival} />
        }
        {showOnly !== LineTimeType.FirstLine &&
            <LineTimeView reservation={res} name="Last Line" lineTime={pt => pt.Departure} />
        }
    </div>
);

let LineTimeView = SuperCritical(({ reservation: res, name, lineTime } : { reservation : BerthReservation, name : string, lineTime : (pt : ItineraryPoint) => ScheduleDate }) =>
    <div className="line-time">
        <label className="pc-text-smalllabel line-time-label">{name}</label>
        <span>{res.Itinerary() && tzmoment(lineTime(res.Itinerary()!).Effective()).format(LINE_TIME_FORMAT)}</span>
    </div>
);

let BatchSummaryView = SuperCritical(({ batch, firstReservationPoint: pt, showConflicted } : { batch : RecurringBatch, firstReservationPoint : () => ItineraryPoint | null, showConflicted? : boolean }) =>
    <div className={cx('batch-summary', {
            conflicted: !!showConflicted
        })}>
        <VesselNameView point={pt} />
        <BerthNameView point={pt} />
        <div className="batch-detail-line">
            {batch.ReservationPoints().length} Reservations
        </div>
        <div className="batch-detail-line">
            {batch.RecurrencePeriod.toString()}
        </div>
    </div>
);

let TopicCardSummaryView = SuperCritical(({ card } : { card : TopicCard }) =>
    <div className="topic-card-summary">
        <div className="vessel">{card.PortCall() && card.PortCall()!.DisplayVesselIdentifier()}</div>
        <div className="card-title">{card.Title()}</div>
    </div>
);

let VesselNameView = SuperCritical(({ point: pt } : { point : () => ItineraryPoint | null }) =>
    <div className="vessel">{pt() && pt()!.PortCall() && pt()!.PortCall()!.DisplayVesselIdentifier()}</div>
);

let BerthNameView = SuperCritical(({ point: pt } : { point : () => ItineraryPoint | null }) => {
    const loc = pt() && pt()!.Location();
    return loc &&
        <div className="berth">
            {loc.Port() && loc.Port()!.Name()}
            {' '}
            {loc.Name()}
        </div>;
});


// WEBPACK FOOTER //
// ./apps/portcall/berthmgrdashboard/activityfeedview.tsx